/**
 * useScrollbarAutohide — mount once from Layout.js.
 *
 * Adds `.is-scrolling` to `<html>` whenever the user actively scrolls
 * anywhere in the app, and removes it a short debounce later. Paired with
 * the scrollbar CSS in `index.css`, this produces macOS-style overlay
 * scrollbars that fade in on interaction and disappear when idle.
 */
import { useEffect } from 'react';

const IDLE_DELAY_MS = 900;

export default function useScrollbarAutohide() {
  useEffect(() => {
    const root = document.documentElement;
    let idleTimer = null;

    const markScrolling = () => {
      root.classList.add('is-scrolling');
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        root.classList.remove('is-scrolling');
      }, IDLE_DELAY_MS);
    };

    // Window scroll (the main page)
    window.addEventListener('scroll', markScrolling, { passive: true, capture: true });
    // Wheel / touchmove — catches scroll intent on nested containers even
    // when their own scroll event doesn't bubble.
    window.addEventListener('wheel', markScrolling, { passive: true });
    window.addEventListener('touchmove', markScrolling, { passive: true });

    return () => {
      window.removeEventListener('scroll', markScrolling, { capture: true });
      window.removeEventListener('wheel', markScrolling);
      window.removeEventListener('touchmove', markScrolling);
      if (idleTimer) clearTimeout(idleTimer);
      root.classList.remove('is-scrolling');
    };
  }, []);
}
